import { APP_BASE_HREF } from '@angular/common';
import {
    Component,
    forwardRef,
    OnChanges,
    KeyValueDiffers,
    Inject,
    OnInit,
    OnDestroy,
    ChangeDetectorRef,
    HostListener,
    Output,
    EventEmitter,
    ElementRef
} from '@angular/core';
import * as am4core from '@amcharts/amcharts4/core';

import { catchError, map, Observable, of } from 'rxjs';

import { BlockComponent } from '../../../PageBuilder/components/Block/block.component';
import { ChartDisplayElementModel, ChartValidSourceStateEnum } from '../../models/chart-display-element.model';
import { AbstractPageBuilderService } from '../../../PageBuilder/services/abstract-page-builder.service';
import { ChartModel } from '../../../Charts/models/chart.model';
import { ReportBlockTypeEnum } from '../../enums/report-block-type.enum';
// Type only, and it has to stay that way. The service reaches reports-block-types.model, which
// imports every ChartBlockComponent subclass - so importing it as a value makes this base class file
// depend on its own subclasses, and whichever one the bundler evaluates first extends undefined.
// It is injected as AbstractPageBuilderService and only ever cast to this type; see mapBlockToChartComponent below.
import type { ReportsPageBuilderService } from '../../services/reports-page-builder.service';
import { ReportsSeriesPreviewService } from '../../services/report-series-preview.service';
import { ChartExportEventModel } from '../../../Charts/models/chart-export-event.model';
import { ReportsChartOptionsService } from '../../services/reports-chart-options.service';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { GeographyTypesEnum } from '../../../../api/journodex/enum/geography-types.enum';
import { ChartMetadataModel } from '../../../Charts/models/chart-metadata.model';
import { MapChartModel } from '../../../Charts/models/map-chart.model';
import { ReportDateRangeModel } from '../../models/report-date-range.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { CategoryValueXYChartOptions } from '../../../Charts/models/barChart/category-value-x-y-chart-options.model';
import { ChartMetricsEnum, chartMetricsToDateAndValueXYChartUnitMap } from '../../enums/chart-metrics.enum';
import { SeriesTypeEnum } from '../../enums/series-type.enum';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { ProminenceLockerService } from '../../../Misc/services/prominence-locker.service';
import { ProminenceRecalculationJobStatus } from '../../../../api/monitoring/monitoring-account-configuration.api';
import { HeatChartModel } from '../../../Charts/models/heatChart/heat-chart.model';
import { ExternalChartMetricsEnum,
    externalChartMetricToTopReferralSourcesReportRequestTypeMap,
    externalChartMetricToTopSocialReferrersReportRequestTypeMap} from '../../enums/external-chart-metrics.enum';
import { MonitorConfigService } from '../../../MonitorShared/services/monitor-config.service';
import { UserPreferencesService } from '../../../../Account/UserPreferences/services/user-preferences.service';
import { userAppPreferencesDefault } from '../../../../Core/Users/data/user-app-preferences-default';
import {
    UserAppPreferenceChartColors
} from '../../../../Core/Users/models/user-app-preferences-chart-colors.model';
import { ClickOutsideService } from '../../../../Common/PageBuilder/services/click-outside.service';
import { DateQueryTypeEnum } from '../../../../api/mentions/enum/date-query-type.enum';
import { ReportsChartDetailService } from '../../services/reports-chart-detail.service';
import { WordCloudChartModel } from '../../../Charts/models/WordCloudChart/word-cloud-chart.model';
import { WordCloudSeriesModel } from '../../../Charts/models/WordCloudChart/word-cloud-series.model';
import { SocialChartDetailsDialogService } from '../../../../Social/Intelligence/services/social-chart-details-dialog.service';
import { ConfigService } from '../../../../Core/Config/services/config.service';
import { AbstractChartOptionsModel } from '../../../Charts/models/abstract-chart-options.model';
import { ActionKeys } from '../../../Forms/enums/keys.enum';
import { DataConnectionService } from '../../../../Core/Users/services/data-connection.service';
import { ReportsChartSeriesConverterService } from '../../../../Common/Reports/services/reports-chart-series-converter.service';
import { DateValueXYChart } from '../../../Charts/models/date-value-x-y-chart.model';
import { CategoryValueXYChart } from '../../../Charts/models/category-value-x-y-chart.model';
import { stackedBarColorsConfig } from '../../../../Common/Charts/config/stacked-bar-colors.config';
import { MediaTypeEnum } from '../../../../Common/Basic/enums/media-type.enum';
import { mediaTypeColorKeyMap } from '../../../Basic/enums/media-type.enum';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { PieChart } from '../../../../Common/Charts/models/pieChart/pie-chart-new.model';
import { ReportsSeriesPreviewNewService } from '../../../../Common/Reports/services/reports-series-preview-new.service';
import { newDataProcessingWorkflowBlockTypes } from '../../../../Common/Reports/models/new-data-processing-workflow-block-types.model';
import { ChartSpecificTypesEnum } from '../../../../Common/Reports/enums/chart-specific-types.enum';
import { SearchCriteriaQuerySentimentsEnum } from '@api/mentions/enum/search-criteria-query-sentiments.enum';
import { chartColors } from '../../../../Common/Charts/enums/chart-colors.enum';
import { ReportSeriesModel } from '../../../../Common/Reports/models/report-series.model';

@Component({
    selector: 'agpr-chart-block',
    templateUrl: './chart-block.component.html',
    styleUrls: ['./chart-block.component.scss'],
    providers: [
        {
            provide: BlockComponent,
            useExisting: forwardRef(() => ChartBlockComponent)
        }
    ],
    standalone: false
})
export class ChartBlockComponent extends BlockComponent implements OnInit, OnChanges, OnDestroy {

    @Output() outputEvent = new EventEmitter<any>();

    displayElement: ChartDisplayElementModel;
    chart: ChartModel;
    reportSeries: ReportSeriesModel[];
    lastDisplayElement: ChartDisplayElementModel;

    private userHasDataConnectors: boolean = false;
    private showExternalDataCustomPlaceholder: boolean = false;
    private externalDataCustomPlaceholderConfig = {
        prefixText: this.localizationService.transform('Reports.chart.dataConnectorPlaceholder'),
        linkText: this.localizationService.transform('Reports.chart.dataConnectorLink'),
        linkUrl: '', // initialized later as needed, looks like appBaseHref isn't really available here
    };
    protected userChartColors$: Observable<UserAppPreferenceChartColors>;
    private userChartColors: UserAppPreferenceChartColors;

    prominenceStatus: ProminenceRecalculationJobStatus;

    isProminenceChart: boolean = false;
    hasProminenceProvision: boolean = false;
    noDataSource: boolean = false;
    noProminenceContactLink: string;
    chartOptions: AbstractChartOptionsModel = {};
    missingDataSource: boolean = false;
    noData: boolean = false;

    readonly messageSeverityEnum = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly prominenceStatuses = ProminenceRecalculationJobStatus;

    constructor(
        public differs: KeyValueDiffers,
        public pageBuilderService: AbstractPageBuilderService,
        public reportSeriesPreviewService: ReportsSeriesPreviewService,
        public reportsChartOptionsService: ReportsChartOptionsService,
        public localizationService: LocalizationService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        public changeDetectorRef: ChangeDetectorRef,
        public userStateService: CurrentUserStateService,
        public prominenceLockerService: ProminenceLockerService,
        public monitorConfigService: MonitorConfigService,
        protected userPreferencesService: UserPreferencesService,
        public clickOutsideService: ClickOutsideService,
        public reportsChartDetailService: ReportsChartDetailService,
        public socialChartDetailsDialogService: SocialChartDetailsDialogService,
        public configService: ConfigService,
        public el: ElementRef,
        public dataConnectionService: DataConnectionService,
        public reportsChartSeriesConverterService: ReportsChartSeriesConverterService,
        public reportsSeriesPreviewNewService: ReportsSeriesPreviewNewService
    ) {
        super(differs, pageBuilderService, changeDetectorRef, clickOutsideService, el);

        this.subscriptions.push(
            this.dataConnectionService.dataConnectors$.subscribe(connectors => {
                if (connectors && connectors.length >= 0) {
                    this.userHasDataConnectors = true;
                } else {
                    this.userHasDataConnectors = false;
                }
            }),
            this.userPreferencesService.getUserAppPreferences().pipe(
                map(userAppPrefs => userAppPrefs.reportsPrefs.defaultChartColors),
                catchError(() => of(userAppPreferencesDefault.reportsPrefs.defaultChartColors))
            ).subscribe(colors => {
                this.chartOptions.defaultColors = colors.generalColors;
                this.userChartColors = colors;
            }),
            this.userStateService.getUserCurrency().subscribe(currency => this.chartOptions.currencyCode = currency)
        );
    }

    // the view mention link is in amcharts, and we couldn't use our normal way to bind them
    @HostListener('click', ['$event.target']) onClick(event) {
        if (event?.classList?.contains('viewMentionLink')) {
            // we would like to pass the chart instead of the block for drilldown.. if we pass block.. if you re-drilldown,
            // it will close and re-open the dialog, because this gets ran again since it's a block component
            if ((this.pageBuilderService as ReportsPageBuilderService).getChartComponentFromBlock &&
                (this.pageBuilderService as ReportsPageBuilderService).getChartComponentFromBlock(this.block)?.chart
            ) {
                this.reportsChartDetailService.viewCoverageAnalysisDialog(event.href,
                    null,
                    (this.pageBuilderService as ReportsPageBuilderService).getChartComponentFromBlock(this.block)?.chart
                );
            } else {
                this.reportsChartDetailService.viewCoverageAnalysisDialog(event.href, this.block);
            }
            return false;
        }
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.userChartColors$ = this.userPreferencesService.getUserAppPreferences().pipe(
            map(userAppPrefs => userAppPrefs.reportsPrefs.defaultChartColors),
            catchError(() => of(userAppPreferencesDefault.reportsPrefs.defaultChartColors))
        );

        this.subscriptions.push(
            this.monitorConfigService?.getProminenceProvision().subscribe(groupsLimit => {
                this.hasProminenceProvision = groupsLimit > 0;
            }),
            this.userChartColors$.subscribe(userChartColors => {
                this.userChartColors = userChartColors;
            }),
            this.subscribeToBlockChanges().subscribe()
        );

        if (this.block.type.key === ReportBlockTypeEnum.PROMINENCE_OVER_TIME
            || this.block.type.key === ReportBlockTypeEnum.PROMINENCE_BY_REACH) {
            this.isProminenceChart = true;
            this.subscriptions.push(
                this.prominenceLockerService.prominenceStatus$.subscribe(status => {
                    const statusChanged = status !== this.prominenceStatus;
                    this.prominenceStatus = status;
                    if (statusChanged) {
                        this.updateChart();
                    }
                })
            );
        }
    }

    ngOnChanges(): void {
        const validSourceState = (<ChartDisplayElementModel>this.block.displayElement).checkValidSourcesForChartType(<ReportBlockTypeEnum>this.block.type.key);
        this.renderable = validSourceState !== ChartValidSourceStateEnum.NONE;
        this.missingDataSource = validSourceState === ChartValidSourceStateEnum.SOME;

        this.noDataSource = false;
        if (
            !this.renderable
            && (this.block.type.key === ReportBlockTypeEnum.REFERRAL_TREND
                || this.block.type.key === ReportBlockTypeEnum.COVERAGE_IMPACT_TREND
                || this.block.type.key === ReportBlockTypeEnum.TOP_SOCIAL_REFERRERS
                || this.block.type.key === ReportBlockTypeEnum.TOP_REFERRAL_SOURCES)
            && !this.userHasDataConnectors
        ) {
            // if we aren't actually rendering the chart, it's a Google Analytics chart,
            // and the user has no Google Analytics Data Connectors, show a custom placeholder message in the block
            this.externalDataCustomPlaceholderConfig.linkUrl = this.appBaseHref + 'DataConnector';
            this.showExternalDataCustomPlaceholder = true;
        } else {
            this.showExternalDataCustomPlaceholder = false;
            this.noDataSource = true;
        }

        if (this.block.type.key === ReportBlockTypeEnum.PROMINENCE_OVER_TIME
            || this.block.type.key === ReportBlockTypeEnum.PROMINENCE_BY_REACH) {
            this.isProminenceChart = true;
        } else {
            this.isProminenceChart = false;
        }
        if (this.renderable) {
            this.updateChart();
        }

        this.setNoProminenceContactLink();
    }

    dragKeydown(event: KeyboardEvent) {
        if (event.key === ActionKeys.LEFT || event.key === ActionKeys.RIGHT
            || event.key === ActionKeys.UP || event.key === ActionKeys.DOWN) {
            this.outputEvent.emit(event);
        }
    }

    resizeKeydown(event: KeyboardEvent) {
        if (event.key === ActionKeys.LEFT || event.key === ActionKeys.RIGHT
            || event.key === ActionKeys.UP || event.key === ActionKeys.DOWN) {
            this.outputEvent.emit(event);
        }
    }

    isDrilldownSocial(url: string): boolean {
        if (url.includes('Social/themes/theme/')) {
            return true;
        }
        return false;
    }

    /**
     * Performs an action on the block
     *
     * @param action A key for a specific action
     */
    onAction(action: string): void {
        if (!this.block.actionable) {
            return;
        }
        switch (action) {
            case 'edit':
                this.pageBuilderService.selectBlock(this.block);
                break;
            case 'delete':
                this.pageBuilderService.removeBlock(this.block);
                break;
            case 'duplicate':
                this.pageBuilderService.duplicateBlock(this.block);
                break;
        }
    }

    updateChart() {
        this.displayElement = this.block?.displayElement as ChartDisplayElementModel;
        // for prominence chart, when prominenceStatus changed, we should update the chart even though displayElement hasn't changed
        if (this.lastDisplayElement?.isEqual(this.displayElement) && !this.isProminenceChart) {
            // chart hasn't changed, don't make a new one
            return;
        }
        if (this.displayElement?.series?.length < 1) {
            this.chart = null;
            this.lastDisplayElement = null;
            return;
        }

        let series = this.displayElement.series.filter(seriesItem => seriesItem.isValid());
        if (series.length < 1) {
            this.chart = null;
            this.lastDisplayElement = null;
            return;
        }

        series.forEach(_series => {
            _series.metric = this.displayElement.metric;
            _series.metrics = this.displayElement.metrics;
            _series.termType = this.displayElement.termType;
            _series.numOfItems = this.displayElement.numOfItems;
            _series.hasPreviousPeriod = this.displayElement.styles.chartDisplaySettings.previousPeriod;
            _series.chartSpecificType = this.displayElement.chartSpecificType;
        });

        // cache the display element so we can verify if we actually need to refresh the chart
        this.lastDisplayElement = this.displayElement?.clone();
        this.setLabels();

        let colorMap;
        // these checks need to occur before the check for this.preview, and that check has to happen
        // before the switch statement
        // these mutations affect what the preview series looks like, so need to happen first
        switch (this.block.type.key) {
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
                series[0].subSeries = null;
                break;
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN:
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN_COMPARISON:
            case ReportBlockTypeEnum.SENTIMENT_TREND:
                series.forEach(_series => {
                    _series.sentiments = this.displayElement.sentiments;
                });
                break;
            case ReportBlockTypeEnum.REFERRAL_TREND:
            case ReportBlockTypeEnum.COVERAGE_IMPACT_TREND:
                series.forEach(seriesModel => {
                    if (seriesModel.type === SeriesTypeEnum.googleAnalytics) {
                        seriesModel.referralTypes = this.displayElement.referralTypes;
                    } else {
                        seriesModel.referralTypes = [];
                    }
                    seriesModel.subSeries = null;
                });
                break;
            case ReportBlockTypeEnum.TOP_SOCIAL_REFERRERS:
                const newReportType = externalChartMetricToTopSocialReferrersReportRequestTypeMap.get(this.displayElement.metric as ExternalChartMetricsEnum);
                series.forEach(_series => {
                    if (_series.type === SeriesTypeEnum.googleAnalytics) {
                        _series.externalData.googleAnalyticsReportType = newReportType;
                        _series.metric = this.displayElement.metric;
                    }
                });
                break;
            case ReportBlockTypeEnum.TOP_REFERRAL_SOURCES:
                const newRequestType = externalChartMetricToTopReferralSourcesReportRequestTypeMap.get(this.displayElement.metric as ExternalChartMetricsEnum);
                series.forEach(_series => {
                    if (_series.type === SeriesTypeEnum.googleAnalytics) {
                        _series.externalData.googleAnalyticsReportType = newRequestType;
                        _series.metric = this.displayElement.metric;
                    }
                });
                break;
            case ReportBlockTypeEnum.SOCIAL_COVERAGE_BY_NETWORK:
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN_COMPARISON:
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN:
                series.forEach(_series => {
                    _series.styles.networkSelection = this.displayElement.networkSelection;
                });
                break;
            case ReportBlockTypeEnum.SHARE_OF_VOICE:
            case ReportBlockTypeEnum.COVERAGE_COMPARISON:
                if (series.every(s => s.styles?.parentGroupId || s.styles?.parentThemeId)) {
                    series.forEach((_series, index) => {
                        colorMap = this.displayElement.styles?.colorMap;
                        if (colorMap) {
                            _series.color = colorMap['subSource' + (index + 1)] || colorMap['seriesColor' + index];
                        }
                        _series.color = _series.color || this.chartOptions.defaultColors?.['color' + (index + 1)];
                    });
                }
                break;
        }

        if (this.preview) {
            if (newDataProcessingWorkflowBlockTypes.includes(this.block.type.key as ReportBlockTypeEnum)) {
                series = this.reportsSeriesPreviewNewService.getPreviewSeries(series, this.block);
            } else {
                series = this.reportSeriesPreviewService.getPreviewSeries(this.block.type.key as ReportBlockTypeEnum,
                    series,
                    this.block.displayElement as ChartDisplayElementModel
                );
            }
        }

        this.constructChart(series);

        // To disable the tolltip in preview (we never save isPreview)
        if (this.chart) {
            this.chart.isPreview = this.preview;
        }
        this.reportSeries = series;

        if ((this.pageBuilderService as ReportsPageBuilderService).mapBlockToChartComponent) {
            // If it's injected as ReportsPageBuilderService it causes a circular dependency, casting works though
            (this.pageBuilderService as ReportsPageBuilderService).mapBlockToChartComponent(this.block, this);
        }
    }

    constructChart(series: ReportSeriesModel[]): void {
        let colorMap;
        switch (this.block.type.key) {
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN:
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN:
            case ReportBlockTypeEnum.SHARE_OF_VOICE:
            case ReportBlockTypeEnum.SOCIAL_COVERAGE_BY_NETWORK:
            case ReportBlockTypeEnum.COVERAGE_COMPARISON:
                this.displayElement = this.block.displayElement as ChartDisplayElementModel;
                this.setColorMapDefaultValues();
                const chartSpecificType = this.displayElement.chartSpecificType;
                const useSubSourceColorMap = [ReportBlockTypeEnum.SHARE_OF_VOICE, ReportBlockTypeEnum.COVERAGE_COMPARISON].includes(this.block.type.key)
                    && series.every(s => s.styles?.parentGroupId || s.styles?.parentThemeId);
                if (chartSpecificType === ChartSpecificTypesEnum.PIE) {
                    const options = this.reportsChartOptionsService.getPieChartOptions(this.displayElement);
                    options.maxNumberChartItems = 7;
                    options.restrictChartItemsToMaxNumber = true;
                    options.hideTooltip = this.hideTooltip;
                    if (useSubSourceColorMap) {
                        options.seriesColorMapKey = 'subSource';
                    }
                    this.chart = new PieChart(
                        series,
                        options,
                        this.getChartMetadata(),
                        this.reportsChartSeriesConverterService.getChartSeries(series, this.block)
                    );
                } else {
                    const options = this.reportsChartOptionsService.getOriginalPieChartBarColumnVisualizationOptions(this.displayElement, this.block.type.key);
                    options.hideTooltip = this.hideTooltip;
                    const chartMetadata = this.getChartMetadata();
                    const chartSerie = this.reportsChartSeriesConverterService.getChartSeries(series, this.block);
                    if (
                        this.block.type.key === ReportBlockTypeEnum.SHARE_OF_VOICE
                        || this.block.type.key === ReportBlockTypeEnum.COVERAGE_COMPARISON
                    ) {
                        options.useReportSeriesColor = true;
                        options.sharedReportChartMinHeight = 450;
                    }
                    if (useSubSourceColorMap) {
                        options.seriesColorMapKey = 'subSource';
                    }
                    if (this.block.type.key === ReportBlockTypeEnum.SOCIAL_COVERAGE_BY_NETWORK) {
                        options.imageAsCategory = !options.stacked;
                        options.appBaseHref = this.appBaseHref;
                        if (this.displayElement.metric === ChartMetricsEnum.ENGAGEMENT_RATE) {
                            options.symbol = '%';
                        }
                        options.resourceLinkOnNewLine = true;
                        options.sharedReportChartMinHeight = 450;
                    }

                    this.chart = new CategoryValueXYChart(
                        series,
                        options,
                        chartMetadata,
                        chartSerie
                    );
                }
                break;
            case ReportBlockTypeEnum.SENTIMENT_TREND:
                const sentimentTrendOptions = this.reportsChartOptionsService.getSentimentTrendOptions(this.displayElement, series[0].sentiments);
                sentimentTrendOptions.hideTooltip = this.hideTooltip;
                this.chart = new DateValueXYChart(
                    series,
                    sentimentTrendOptions,
                    this.getChartMetadata(),
                    this.reportsChartSeriesConverterService.getChartSeries(series, this.block)
                );
                break;
            case ReportBlockTypeEnum.TOP_10_OUTLETS:
            case ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME:
            case ReportBlockTypeEnum.TOP_HASHTAGS:
            case ReportBlockTypeEnum.TOP_SOCIAL_REFERRERS:
                const top10ChartOptions: CategoryValueXYChartOptions = this.reportsChartOptionsService.getTop10ChartOptions(this.displayElement);
                top10ChartOptions.hideTooltip = this.hideTooltip;
                if (this.block.type.key === ReportBlockTypeEnum.TOP_SOCIAL_REFERRERS) {
                    top10ChartOptions.label = this.localizationService.transform('Reports.googleAnalyticsCharts.topSocialReferrals.label');
                    top10ChartOptions.resourceLinkOnNewLine = true;
                }
                if (this.displayElement.metric === ChartMetricsEnum.domainAuthority) {
                    top10ChartOptions.noPercentage = true;
                }
                this.chart = new CategoryValueXYChart(
                    series,
                    top10ChartOptions,
                    this.getChartMetadata(),
                    this.reportsChartSeriesConverterService.getChartSeries(series, this.block)
                );
                break;
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
                let unit;
                if (series[0]?.metrics?.length > 0) {
                    unit = chartMetricsToDateAndValueXYChartUnitMap.get(series[0].metrics[0]);
                }
                const coverageOverTimeOptions = this.reportsChartOptionsService.getCoverageTrendChartOptions(
                    unit,
                    this.displayElement,
                    series[0].metrics
                );
                coverageOverTimeOptions.hideTooltip = this.hideTooltip;
                this.chart = new DateValueXYChart(
                    series,
                    coverageOverTimeOptions,
                    this.getChartMetadata(),
                    this.reportsChartSeriesConverterService.getChartSeries(series, this.block)
                );
                break;
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
                const trendComparisonChartOptions = this.reportsChartOptionsService.getCoverageTrendChartOptions(
                    chartMetricsToDateAndValueXYChartUnitMap.get(series[0].metric),
                    this.displayElement
                );
                trendComparisonChartOptions.yValueLabel = this.reportsChartOptionsService.getLabelByMetric(series[0].metric, this.displayElement);
                trendComparisonChartOptions.hideTooltip = this.hideTooltip;

                this.chart = new DateValueXYChart(
                    series,
                    trendComparisonChartOptions,
                    this.getChartMetadata(),
                    this.reportsChartSeriesConverterService.getChartSeries(series, this.block)
                );
                break;
            case ReportBlockTypeEnum.GEOGRAPHY_BREAKDOWN_BY_VOLUME:
                this.displayElement = this.block.displayElement as ChartDisplayElementModel;
                let mapChartOptions;
                if (this.displayElement.chartSpecificType === ChartSpecificTypesEnum.MAP) {
                    mapChartOptions = this.reportsChartOptionsService.getMapChartOptions(this.displayElement);
                    mapChartOptions.hideTooltip = this.hideTooltip;
                    colorMap = mapChartOptions.styles.colorMap;
                    if (colorMap['region10'] || this.userChartColors?.['generalColors']?.['color10']) {
                        colorMap['region10'] = colorMap['region10'] || this.userChartColors?.['generalColors']?.['color10'];
                    }
                    if (this.preview) {
                        colorMap['backgroundColor'] = chartColors.agprWhite;
                    }
                    if (series && series.length && series[0].styles) {
                        if (series[0].styles.regionIsoCode) {
                            mapChartOptions.regionIsoCode = series[0].styles.regionIsoCode + '';
                        }
                        if (series[0].styles.geographyScale) {
                            mapChartOptions.geographyScale = <GeographyTypesEnum>series[0].styles.geographyScale;
                        }
                    }
                    this.chart = new MapChartModel(
                        series,
                        mapChartOptions,
                        this.getChartMetadata()
                    );
                } else {
                    mapChartOptions = this.reportsChartOptionsService.getTop10ChartOptions(this.displayElement);
                    mapChartOptions.hideTooltip = this.hideTooltip;
                    colorMap = mapChartOptions.styles.colorMap;
                    if (colorMap['region10'] || this.userChartColors?.['generalColors']?.['color10']) {
                        colorMap['region10'] = colorMap['region10'] || this.userChartColors?.['generalColors']?.['color10'];
                    }
                    this.chart = new CategoryValueXYChart(
                        series,
                        mapChartOptions,
                        this.getChartMetadata(),
                        this.reportsChartSeriesConverterService.getChartSeries(series, this.block)
                    );
                }
                break;
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN_COMPARISON:
                this.setColorMapDefaultValues();
                const sentimentBreakdownComparisonOptions = this.reportsChartOptionsService.getSentimentBreakDownComparisonOptions(this.displayElement);
                sentimentBreakdownComparisonOptions.hideTooltip = this.hideTooltip;
                this.chart = new CategoryValueXYChart(
                    series,
                    sentimentBreakdownComparisonOptions,
                    this.getChartMetadata(),
                    this.reportsChartSeriesConverterService.getChartSeries(series, this.block)
                );
                break;
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN_COMPARISON:
                this.setColorMapDefaultValues();
                const mediaTypeBreakdownComparisonOptions = this.reportsChartOptionsService.getMediaTypeBreakDownComparisonOptions(this.displayElement);
                mediaTypeBreakdownComparisonOptions.hideTooltip = this.hideTooltip;
                this.chart = new CategoryValueXYChart(
                    series,
                    mediaTypeBreakdownComparisonOptions,
                    this.getChartMetadata(),
                    this.reportsChartSeriesConverterService.getChartSeries(series, this.block)
                );
                break;
            case ReportBlockTypeEnum.COVERAGE_IMPACT_TREND:
                const coverageImpactTrendOptions = this.reportsChartOptionsService.getCoverageImpactTrendOptions(this.displayElement);
                coverageImpactTrendOptions.hideTooltip = this.hideTooltip;
                coverageImpactTrendOptions.styles['legendItemWidth'] = 150;
                this.chart = new DateValueXYChart(
                    series,
                    coverageImpactTrendOptions,
                    this.getChartMetadata(),
                    this.reportsChartSeriesConverterService.getChartSeries(series, this.block)
                );
                break;
            case ReportBlockTypeEnum.REFERRAL_TREND:
                const referralTrendOptions = this.reportsChartOptionsService.getReferralTrendOptions(this.displayElement);
                referralTrendOptions.hideTooltip = this.hideTooltip;
                this.chart = new DateValueXYChart(
                    series,
                    referralTrendOptions,
                    this.getChartMetadata(),
                    this.reportsChartSeriesConverterService.getChartSeries(series, this.block)
                );
                break;
            case ReportBlockTypeEnum.PROMINENCE_OVER_TIME:
                const prominenceTrendOptions = this.reportsChartOptionsService.getProminenceOverTimeChartOptions(this.displayElement);
                prominenceTrendOptions.prominenceStatus = this.prominenceStatus;
                this.chart = new DateValueXYChart(
                    series,
                    prominenceTrendOptions,
                    this.getChartMetadata(),
                    this.reportsChartSeriesConverterService.getChartSeries(series, this.block)
                );
                break;
            case ReportBlockTypeEnum.PROMINENCE_BY_REACH:
                const prominenceReachOptions = this.reportsChartOptionsService.getProminencByReachOptions(this.displayElement);
                prominenceReachOptions.prominenceStatus = this.prominenceStatus;
                prominenceReachOptions.hideTooltip = this.hideTooltip;
                this.chart = new HeatChartModel(
                    series,
                    prominenceReachOptions,
                    this.getChartMetadata()
                );
                break;
            case ReportBlockTypeEnum.TOP_REFERRAL_SOURCES:
                let chartSpecificTypeEnum: ChartSpecificTypesEnum;
                let hideValueLabel: boolean;
                let exportLabelTotalKey: string;
                let columnHeightThreshold;
                let categoryLabelVerticalShiftPx;
                switch (this.displayElement.metric) {
                    case ExternalChartMetricsEnum.sessions:
                    case ExternalChartMetricsEnum.users:
                        chartSpecificTypeEnum = this.displayElement.chartSpecificType === ChartSpecificTypesEnum.BAR ?
                            ChartSpecificTypesEnum.BAR : ChartSpecificTypesEnum.COLUMN;
                        hideValueLabel = true;
                        categoryLabelVerticalShiftPx = -1;
                        break;
                    case ExternalChartMetricsEnum.sessionsNewReturning:
                    case ExternalChartMetricsEnum.usersNewReturning:
                        chartSpecificTypeEnum = this.displayElement.chartSpecificType === ChartSpecificTypesEnum.BAR ?
                            ChartSpecificTypesEnum.MULTI_STACKED_BAR : ChartSpecificTypesEnum.MULTI_STACKED_COLUMN;
                        hideValueLabel = false;
                        columnHeightThreshold = 13;
                        categoryLabelVerticalShiftPx = -2;
                        break;
                }
                if (this.displayElement.metric === ExternalChartMetricsEnum.sessions
                    || this.displayElement.metric === ExternalChartMetricsEnum.sessionsNewReturning) {
                    exportLabelTotalKey = ExternalChartMetricsEnum.sessions;
                } else {
                    exportLabelTotalKey = ExternalChartMetricsEnum.users;
                }
                const topReferralSourcesOptions = this.reportsChartOptionsService.getReferralSourcesOptions(
                    this.displayElement,
                    this.block.type.key,
                    chartSpecificTypeEnum
                );
                topReferralSourcesOptions.hideTooltip = this.hideTooltip;
                topReferralSourcesOptions.hideValueLabel = hideValueLabel;
                topReferralSourcesOptions.exportLabelTotalKey = exportLabelTotalKey;
                topReferralSourcesOptions.showFewerItemsAtLimitedSpace = true;
                if (columnHeightThreshold) {
                    topReferralSourcesOptions.styles.columnHeightThreshold = columnHeightThreshold;
                }
                topReferralSourcesOptions.styles.categoryLabelVerticalShiftPx = categoryLabelVerticalShiftPx;
                topReferralSourcesOptions.styles.legendItemWidth = 120;
                const metaData = this.getChartMetadata();
                if (this.displayElement?.metric) {
                    let metricLabel = this.displayElement.metric.split('_')[0];
                    metricLabel = '[bold]' + metricLabel.charAt(0) + metricLabel.slice(1).toLowerCase();
                    metaData.secondarySeriesLabelText = metaData.primarySeriesLabelText;
                    metaData.secondarySeriesUrl = metaData.primarySeriesUrl;
                    metaData.primarySeriesLabelText = metricLabel;
                    metaData.primarySeriesUrl = '';
                }
                const chartSeries = this.reportsChartSeriesConverterService.getChartSeries(series, this.block);

                this.chart = new CategoryValueXYChart(
                    series,
                    topReferralSourcesOptions,
                    metaData,
                    chartSeries
                );
                break;
            case ReportBlockTypeEnum.TOP_TERMS:
                const topTermsOptions = this.reportsChartOptionsService.getTopTermsOptions(this.displayElement);
                topTermsOptions.hideTooltip = this.hideTooltip;
                this.chart = new WordCloudChartModel(
                    series as WordCloudSeriesModel[],
                    topTermsOptions,
                    this.getChartMetadata()
                );
                break;
            default:
                this.chart = null;
                break;
        }
    }

    /**
     * Handles the export button click action, exporting the chart in the selected format
     */
    public chartExport(event: ChartExportEventModel) {
        this.chart.export(event.eventKey.toLowerCase() as keyof am4core.IExportOptions, this.displayElement.title);
    }

    /**
     * Cache the labels so that we don't have to call a function in the template
     */
    protected setLabels(): void {
        if (this.displayElement?.series?.[0]?.dateRange
                // For shared report, we already set its relative and absolute date rage when mapping api blocks
                // For date range "ALL" scenario, we cannot do localization in model, so we keep it here
                && (!this.displayElement.series[0].isSharedReport || this.displayElement?.series?.[0]?.dateRange.type === DateQueryTypeEnum.ALL)
        ) {
            this.displayElement.dateRangeLabel = this.reportSeriesPreviewService.dateRangeToString(
                this.displayElement.series, this.block.type.key as ReportBlockTypeEnum
            );
        }
    }

    /**
     * Gets the export image for the full report pdf export
     * @NOTE don't remove the width/height params.
     */
    public async getFullExportImage(
        width: number,
        height: number,
        transparent: boolean = false
    ): Promise<string> {
        if (!this.chart) {
            return null;
        }

        return await this.chart.getPNGImageData(1, null, null, transparent);
    }

    /**
     * Gets the series label from the chart
     */
    public getExportSeriesLabel(): string {
        return this.chart ? this.chart.getExportSeriesLabel() : '';
    }

    public getChartMetadata(): ChartMetadataModel {
        if (!this.displayElement || !this.displayElement.series || !this.displayElement.series[0]) {
            return null;
        }

        const singleSeries = this.displayElement.series.length === 1;
        const externalSource = this.displayElement.series.find(series => series.hasValidExternalSource());

        let primarySeriesLabelText, secondarySeriesLabelText, primarySeriesUrl, secondarySeriesUrl;
        if (externalSource) {
            primarySeriesLabelText = this.displayElement.series[0].label;
            primarySeriesUrl = this.displayElement.series[0].getSeriesUrl();

            if (!singleSeries) {
                secondarySeriesLabelText = this.displayElement.series[1].label;
                secondarySeriesUrl = this.displayElement.series[1].getSeriesUrl();
            }
        } else if (singleSeries) {
            primarySeriesLabelText = this.displayElement.series[0].label;
            primarySeriesUrl = this.displayElement.series[0].getSeriesUrl(false);
        }

        const noContent = this.localizationService.transform('Monitoring.result.noContentFound');
        const noContentLabel = noContent;

        const noDataFromDateRangeLabel = this.localizationService.transform('Reports.chart.noDataFromDateRange');

        return {
            type: this.block.type.key,
            id: this.id,
            titleText: this.displayElement.title,

            // @NOTE - if date range becomes per-series, this will have to change
            dateRange: ReportDateRangeModel.clone(this.displayElement.series[0].dateRange),
            dateRangeLabelText: this.displayElement.dateRangeLabel,

            primarySeriesLabelText: primarySeriesLabelText,
            primarySeriesUrl: primarySeriesUrl ? this.appBaseHref + primarySeriesUrl : null,
            secondarySeriesLabelText: secondarySeriesLabelText,
            secondarySeriesUrl: secondarySeriesUrl ? this.appBaseHref + secondarySeriesUrl : null,

            labelDisplayOptions: this.displayElement.labelDisplayOptions,
            stackedChartTypes: this.displayElement.stackedChartTypes,
            enableDataDisplaySettings: this.displayElement.enableDataDisplaySettings,
            noContentLabel: noContentLabel,
            noDataFromDateRangeLabel: noDataFromDateRangeLabel
        };
    }

    setNoProminenceContactLink(): void {
        if (this.configService.brandGUIDIsBurrelles()) {
            this.noProminenceContactLink = 'mailto:inquiry@burrelles.com';
        } else {
            this.noProminenceContactLink = 'https://www.agilitypr.com/contact/';
        }
    }

    formatDateRangeLabel(format: string) {
        if (this.chart?.dateRangeLabel) {
            this.chart.dateRangeLabel.text = this.reportSeriesPreviewService.dateRangeToString(this.displayElement.series, null, format);
        }
    }

    updateNoData(noData: boolean): void {
        this.noData = noData;
    }


    /**
     * For backward compatibility
     */
    setColorMapDefaultValues(): void {
        let colorTypeKey;
        switch (this.block.type.key) {
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN:
            case ReportBlockTypeEnum.SENTIMENT_BREAKDOWN_COMPARISON:
                colorTypeKey = 'sentiments';
                break;
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN:
            case ReportBlockTypeEnum.MEDIA_TYPE_BREAKDOWN_COMPARISON:
                colorTypeKey = 'networkSelection';
                break;
        }

        let colorTypes = this.displayElement[colorTypeKey];
        if (!colorTypes && colorTypeKey === 'networkSelection') {
            colorTypes = Object.values(MediaTypeEnum);
        }
        if (colorTypes?.length > 0 && this.displayElement.styles) {
            if (!this.displayElement.styles.colorMap) {
                this.displayElement.styles.colorMap = {};
            }
            const colorMap = this.displayElement.styles.colorMap;
            const userPrefColorSetKey = colorTypeKey === 'sentiments' ? 'sentimentColors' : 'mediaTypeColors';
            const sentimentEnumToUserPrefColorKeysMap = new Map<SearchCriteriaQuerySentimentsEnum, string>([
                [SearchCriteriaQuerySentimentsEnum.positive, 'positive'],
                [SearchCriteriaQuerySentimentsEnum.neutral, 'neutral'],
                [SearchCriteriaQuerySentimentsEnum.negative, 'negative'],
                [SearchCriteriaQuerySentimentsEnum.notAvailable, 'notAvailable']
            ]);
            colorTypes.forEach(color => {
                const userPrefColorKey = colorTypeKey === 'sentiments' ?
                    sentimentEnumToUserPrefColorKeysMap.get(color)
                    : mediaTypeColorKeyMap.get(color);
                colorMap[color] = colorMap[color] || this.userChartColors?.[userPrefColorSetKey]?.[userPrefColorKey]
                    || stackedBarColorsConfig[color];
            });
        }
    }
}
